import { If } from '@finn/design-system/atoms/If';
import { cn, parseToHtml, useIntl } from '@finn/ui-utils';

import { useIsFeatureVisible } from '../../../hooks/useIsFeatureVisible';
import { useValue } from '../../../hooks/useValue';
import { getFormattedDate } from '../../../services/dates';
import { FieldPath, VisibilityConfig } from '../../../types';
import { Availability } from '../../../types/base';
import { Divider } from '../../Divider';
import { DataItemBlock, DataItemBlockProps } from './DataItemBlock';

type Item =
  | DataItemBlockProps
  | {
      variant: 'text';
      label?: string;
      value?: FieldPath;
      hidden?: VisibilityConfig;
    }
  | {
      label: string;
      value?: FieldPath;
      availabilities: FieldPath;
      appointment_date: FieldPath;
      slot: FieldPath;
      variant: 'slot';
    };

export type DataBlockProps = {
  type: 'data-block';
  title?: string;
  items: (Item | string)[];
};

const SlotItem = ({
  label,
  availabilities,
  appointment_date,
  slot,
}: {
  label: string;
  availabilities: FieldPath;
  appointment_date: FieldPath;
  slot: FieldPath;
}) => {
  const { formatMessage } = useIntl();
  const [availabilitiesItems] = useValue<Availability[]>(availabilities);
  const [appointmentPreference] = useValue(appointment_date);
  const [slotPreference] = useValue(slot);

  const slots = availabilitiesItems?.map((item) => item.available_slots).flat();
  const slotLabel = slots?.find(
    (item) => item.value === slotPreference
  )?.formatted_time;
  const dateLabel = getFormattedDate(appointmentPreference, 'DD.MM.YYYY');

  return (
    <p className="whitespace-pre-line body-16-light">
      {formatMessage(label, {
        apointment: dateLabel || '-',
        slot: slotLabel || '-',
      })}
    </p>
  );
};

// universal block knows how to display more complex items
// like ['add', {value1}, {value2}] + respects variants, like price formating
const UniversalBlock = ({ item }: { item: Item | string }) => {
  const value = typeof item === 'string' ? item : item.value;
  const [simpleValue] = useValue(typeof value === 'string' ? value : '');
  const isHidden = useIsFeatureVisible(
    typeof item === 'object' && 'hidden' in item ? item.hidden : {}
  );

  if (isHidden) {
    return null;
  }

  if (typeof item === 'string') {
    return (
      <p className="whitespace-pre-line body-16-light">
        {parseToHtml(simpleValue)}
      </p>
    );
  }

  if (item.variant === 'disclaimer') {
    return (
      <p className="whitespace-pre-line body-12-light">
        {parseToHtml(simpleValue)}
      </p>
    );
  }

  if (item.variant === 'slot') {
    return <SlotItem {...item} />;
  }

  if (typeof item !== 'string' && item.variant === 'text') {
    return (
      <p className="whitespace-pre-line body-16-light">
        {parseToHtml(simpleValue)}
      </p>
    );
  }

  return <DataItemBlock item={item} />;
};

export const DataBlock = ({ title, items }: DataBlockProps) => {
  const hasRows = items.some(
    (item) =>
      item &&
      typeof item === 'object' &&
      'variant' in item &&
      item.variant === 'row'
  );

  return (
    <div className={cn('flex flex-col', hasRows ? 'gap-0 pb-4' : 'gap-4')}>
      {title ? <h6 className="global-t6-semibold">{title}</h6> : null}
      {items?.map((item) => (
        <UniversalBlock
          key={typeof item === 'string' ? item : String(item.label)}
          item={item}
        />
      ))}
      <If condition={!hasRows}>
        <Divider
          className={
            items?.some(
              (item) => typeof item === 'object' && 'badgeLabel' in item
            )
              ? 'mb-0 mt-2'
              : 'my-4'
          }
        />
      </If>
    </div>
  );
};
